import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import AuthService from '@/services/AuthService'
import router from '@/router'
import { setSentryUser } from '@/lib/sentry'

interface User {
    id: number
    name: string
    email: string
    roles?: Array<{ name: string }>
    permissions?: Array<{ name: string }>
}

export const useAuthStore = defineStore('auth', () => {
    const user = ref<User | null>(null)
    const isLoading = ref(false)
    const isInitialized = ref(false)
    const isGoogleScriptLoaded = ref(false)

    const isAuthenticated = computed(() => !!user.value)
    const userRoles = computed(() => user.value?.roles?.map(r => r.name) || [])

    const hasRole = (role: string) => userRoles.value.includes(role)
    const hasAnyRole = (roles: string[]) => roles.some(role => hasRole(role))

    const login = async (credentials: { email: string; password: string; remember?: boolean }) => {
        isLoading.value = true
        try {
            await AuthService.login(credentials)
            await fetchUser()
            isInitialized.value = true
            return true
        } catch (error) {
            throw error
        } finally {
            isLoading.value = false
        }
    }

    const logout = async () => {
        try {
            await AuthService.logout()
        } catch (error) {
            console.error('Logout error:', error)
        } finally {
            user.value = null
            setSentryUser(null)
            await router.push('/login')
        }
    }

    const fetchUser = async () => {
        try {
            const response = await AuthService.getUser()
            user.value = response.data
            if (user.value) {
                setSentryUser({ id: user.value.id, email: user.value.email, name: user.value.name })
            }
        } catch (error) {
            user.value = null
            throw error
        }
    }

    const initializeAuth = async () => {
        if (isInitialized.value) return

        // If we already have user data, no need to fetch again
        if (user.value) {
            isInitialized.value = true
            return
        }

        try {
            await fetchUser()
        } catch (error) {
            // User not authenticated, that's okay
        } finally {
            isInitialized.value = true
        }
    }

    const validateSession = async () => {
        try {
            await AuthService.ping()
            return true
        } catch (error) {
            user.value = null
            return false
        }
    }

    const googleLogin = async (data: object) => {
        isLoading.value = true
        try {
            await AuthService.validateGoogleToken(data)
            await fetchUser()
            return true
        } catch (error) {
            throw error
        } finally {
            isLoading.value = false
        }
    }

    const setUser = (userData: User | null) => {
        user.value = userData
        isInitialized.value = true
    }


    return {
        user: readonly(user),
        isLoading: readonly(isLoading),
        isInitialized: readonly(isInitialized),
        isGoogleScriptLoaded,
        isAuthenticated,
        userRoles,
        hasRole,
        hasAnyRole,
        login,
        logout,
        fetchUser,
        initializeAuth,
        validateSession,
        googleLogin,
        setUser
    }
})